import { QueryKey, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { CountryConfigurationUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrls';

import { useApiCountryConfigurationCurrentRetrieve } from '../../../apiClient/latest/api-country-configuration-current-retrieve';
import { useApiCountryConfigurationNavigationRetrieve } from '../../../apiClient/latest/api-country-configuration-navigation-retrieve';
import { ErrorType } from '../../Network/orvalHTTPInterface';

const METERS_IN_MILE = 1609.34;
const METERS_IN_KM = 1000;

// UTILS //

export const isEU = (configuration?: CountryConfiguration): boolean => {
  if (!configuration) return false;
  return configuration?.continentCode === 'EU';
};

export const convertLocalizedToMeters = (
  value: number,
  configuration?: CountryConfiguration
): number => {
  if (!configuration || configuration.distanceUnit === 'mi') {
    return Math.round(value * METERS_IN_MILE);
  }
  if (configuration.distanceUnit === 'km') {
    return value * METERS_IN_KM;
  }
  return value;
};

export const convertMetersToLocalized = (
  value: number,
  configuration?: CountryConfiguration
): number => {
  // Round values to nearest 10th, accounting for floating point errors
  if (!configuration || configuration.distanceUnit === 'mi') {
    return Math.round((value / METERS_IN_MILE + Number.EPSILON) * 10) / 10;
  }
  if (configuration.distanceUnit === 'km') {
    return Math.round((value / METERS_IN_KM + Number.EPSILON) * 10) / 10;
  }
  return value;
};

// HOOKS //

export type UseCurrentCountryConfiguration = UseQueryResult<
  CountryConfiguration,
  ErrorType<unknown>
> & {
  queryKey: QueryKey;
  emergencyHotline?: string;
  urls?: CountryConfigurationUrls;
};

export const useCurrentCountryConfiguration = (
  queryOptions: UseQueryOptions<CountryConfiguration, ErrorType<unknown>, CountryConfiguration> = {}
): UseCurrentCountryConfiguration => {
  const query = useApiCountryConfigurationCurrentRetrieve({ query: queryOptions });
  const emergencyHotline = query.data?.phoneNumbers?.emergencyHotlinePhoneFormatted;
  const urls = query.data?.urls;
  return { ...query, emergencyHotline, urls };
};

export type UseNavigationCountryConfiguration = UseQueryResult<
  CountryConfiguration,
  ErrorType<unknown>
> & {
  queryKey: QueryKey;
  availableLanguages: string[][] | undefined;
  isEU: boolean;
  urls: CountryConfigurationUrls | undefined;
};

export const useNavigationCountryConfiguration = (
  queryOptions: UseQueryOptions<CountryConfiguration, ErrorType<unknown>, CountryConfiguration> = {}
): UseNavigationCountryConfiguration => {
  const query = useApiCountryConfigurationNavigationRetrieve({ query: queryOptions });
  const availableLanguages = query.data?.availableLanguages;
  const isEurope = isEU(query.data);
  const urls = query.data?.urls;
  return { ...query, availableLanguages, isEU: isEurope, urls };
};
